import { useTranslation } from 'react-i18next';
import { Button, Text } from '@/shared/ui';
import { formatCents } from '@/shared/utils/format-cents';
import { formatLocalizedDate } from '@/shared/utils/date';
import { useActivateNow } from '../../hooks/useActivateNow';
import type { ActivatePhase, ActivateState } from '../plan-v2/activate-state';
import { nextChargeAfter } from '../plan-v2/next-charge';
import type { TrialOffer } from '../plan-v2/trial-offer';
import { PlanFlowShell } from '../plan-flow/PlanFlowShell';

interface Props {
  agencyId: string;
  offer: TrialOffer;
  onClose: () => void;
  /** The trial is over and paid: reload the subscription and the usage. */
  onActivated: () => void;
  /** The backend says there is no trial to end: reload so the offer goes away. */
  onNotInTrial: () => void;
  /** "Trocar cartão": leaves this dialog for the card flow. */
  onChangeCard: () => void;
}

type Kind = 'pay' | 'done' | 'changeCard';

/** What the main button says and does in each phase. */
const FOOTER: Record<ActivatePhase, { kind: Kind; label: string }> = {
  confirm: { kind: 'pay', label: 'trial.modal.pay' },
  processing: { kind: 'pay', label: 'trial.modal.processing' },
  success: { kind: 'done', label: 'trial.modal.continue' },
  declined: { kind: 'changeCard', label: 'trial.modal.changeCard' },
  missingCard: { kind: 'changeCard', label: 'trial.modal.addCard' },
  retry: { kind: 'pay', label: 'trial.modal.tryAgain' },
  error: { kind: 'pay', label: 'trial.modal.tryAgain' },
  notInTrial: { kind: 'pay', label: 'trial.modal.pay' },
};

type Translate = ReturnType<typeof useTranslation>['t'];

/** The word with its first letter upper-cased, or an empty string. */
const cap = (word: string | null) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : '');

/** One line of the summary list. */
function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-(--space-3)" style={{
      padding: 'var(--space-3) var(--space-4)', borderBottom: '1px solid var(--border-ultra-subtle)',
    }}>
      <dt style={{ color: 'var(--text-secondary)' }}>{label}</dt>
      <dd style={{
        margin: 0, textAlign: 'right', color: 'var(--text-primary)',
        fontWeight: strong ? 'var(--font-bold)' : 'var(--font-semibold)',
      }}>
        {value}
      </dd>
    </div>
  );
}

/** What the plan will cost and when, before anything is charged. */
function Summary({ offer, t, lang }: { offer: TrialOffer; t: Translate; lang: string }) {
  const next = nextChargeAfter(new Date(), offer.frequency);
  const date = formatLocalizedDate(next, lang);
  const card = offer.lastFour
    ? `${cap(offer.brand)} •••• ${offer.lastFour}`.trim()
    : t('trial.modal.noCard');
  const renews = offer.frequency === 'yearly'
    ? t('trial.modal.nextChargeYearly', { date })
    : t('trial.modal.nextChargeMonthly', { date, day: next.getDate() });
  return (
    <>
      <dl data-testid="activate-summary" style={{
        margin: 0, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)',
        fontSize: 'var(--text-body-sm)',
      }}>
        <Row strong label={t('trial.modal.chargedToday')} value={formatCents(offer.cents, { locale: lang })} />
        <Row label={t('trial.modal.card')} value={card} />
        <Row label={t('trial.modal.nextCharge')} value={renews} />
        <Row label={t('trial.modal.unlocks')} value={t('trial.modal.unlocksValue', {
          interactions: offer.interactions, searches: offer.searches,
        })} />
      </dl>
      <Text component="p" style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--text-secondary)' }}>
        {t('trial.modal.note', { date: formatLocalizedDate(offer.trialEnd, lang) })}
      </Text>
    </>
  );
}

/** The message under the lead for the outcomes that leave the trial as it was. */
function Notice({ state, offer, t, lang }: {
  state: ActivateState; offer: TrialOffer; t: Translate; lang: string;
}) {
  const lines: Partial<Record<ActivatePhase, string[]>> = {
    declined: [
      t('trial.declined.head'),
      t('trial.declined.body', { date: formatLocalizedDate(offer.trialEnd, lang) }),
      state.reason ? t('trial.declined.reason', { reason: state.reason }) : '',
    ],
    retry: [t('trial.retry.lead')],
    missingCard: [t('trial.missingCard.lead')],
    error: [state.snapshot ? t('trial.error.snapshot') : t('trial.error.lead')],
  };
  const shown = (lines[state.phase] ?? []).filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <div role="alert" data-testid="activate-notice" className="flex flex-col gap-(--space-1)" style={{
      padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-subtle)', background: 'var(--surface-secondary)',
      fontSize: 'var(--text-body-sm)',
    }}>
      {shown.map((line, index) => (
        <Text key={line} component="span" c="inherit" style={{ fontWeight: index === 0 ? 'var(--font-semibold)' : undefined }}>{line}</Text>
      ))}
    </div>
  );
}

/** The modal title for the activation phase. */
function titleFor(state: ActivateState, offer: TrialOffer, t: Translate): string {
  if (state.phase === 'success') return t('trial.success.title', { plan: offer.planName });
  if (state.phase === 'declined') return t('trial.declined.title');
  return t('trial.modal.title', { plan: offer.planName });
}

/** The modal lead text for the activation phase, with the charged price on success. */
function leadFor(state: ActivateState, offer: TrialOffer, t: Translate, lang: string): string {
  if (state.phase === 'success') {
    return t('trial.success.lead', { price: formatCents(state.chargedCents ?? offer.cents, { locale: lang }) });
  }
  if (state.phase === 'declined') return t('trial.declined.lead');
  return t(state.phase === 'processing' ? 'trial.modal.leadProcessing' : 'trial.modal.lead');
}

/**
 * The confirmation before the trial is ended and the plan is charged (RN-10).
 *
 * Shows the value, the card and the dates before anything is charged. While
 * the charge runs both buttons are disabled and the dialog cannot be dismissed
 * (AR-05); a decline changes nothing and offers another card.
 *
 * @param props - The offer, the agency and what each outcome should do.
 * @returns The dialog.
 */
export function ActivateNowModal({
  agencyId, offer, onClose, onActivated, onNotInTrial, onChangeCard,
}: Props) {
  const { t, i18n } = useTranslation('features/subscription');
  const lang = i18n.language;
  const { state, activate } = useActivateNow(agencyId);
  const busy = state.phase === 'processing';
  const { phase } = state;

  const pay = async () => {
    const next = await activate();
    if (next?.phase === 'success') onActivated();
    if (next?.phase === 'notInTrial') onNotInTrial();
  };

  const handlers: Record<Kind, () => void> = {
    pay: () => { void pay(); },
    done: onClose,
    changeCard: onChangeCard,
  };
  const { kind, label } = FOOTER[phase];
  const price = formatCents(offer.cents, { locale: lang });

  return (
    <PlanFlowShell
      opened
      narrow
      locked={busy}
      eyebrow={t('trial.modal.eyebrow')}
      title={titleFor(state, offer, t)}
      onClose={onClose}
      footer={(
        <>
          {phase !== 'success' && (
            <Button variant="outline" onClick={onClose} disabled={busy} data-testid="activate-secondary">
              {t(phase === 'confirm' || busy ? 'trial.modal.later' : 'trial.modal.close')}
            </Button>
          )}
          <Button data-testid="activate-primary" onClick={handlers[kind]} disabled={busy}>
            {t(label, { price })}
          </Button>
        </>
      )}
    >
      <Text component="p" style={{ margin: 0, color: 'var(--text-secondary)' }}>
        {leadFor(state, offer, t, lang)}
      </Text>
      {(phase === 'confirm' || busy) && <Summary offer={offer} t={t} lang={lang} />}
      {phase === 'success' && state.cycleEnd && (
        <Text component="p" data-testid="activate-next" style={{ margin: 0, fontSize: 'var(--text-body-sm)' }}>
          {t('trial.success.next', {
            price: formatCents(offer.cents, { locale: lang }),
            date: formatLocalizedDate(state.cycleEnd, lang),
          })}
        </Text>
      )}
      <Notice state={state} offer={offer} t={t} lang={lang} />
    </PlanFlowShell>
  );
}
